import React, { useState } from 'react';
import { createGlobalStyle } from 'styled-components';
import Button from '../shared/Button/Button';
import SaleDetails from './SaleDetails';
import { TicketCard, TicketActions } from './sales.styles';
import { saleStatus } from './saleLabels';
import StatusBadge from '../shared/StatusBadge/StatusBadge';

// Al imprimir solo sale el ticket.
const PrintOnlyTicket = createGlobalStyle`
  @media print {
    body * { visibility: hidden; }
    .sale-ticket-print, .sale-ticket-print * { visibility: visible; }
    .sale-ticket-print {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      box-shadow: none;
      border: none;
    }
    .sale-ticket-print .no-print { display: none !important; }
  }
`;

const dataUrlToBlob = async (dataUrl) => (await fetch(dataUrl)).blob();

// Ticket de una venta: QR para enseñarlo o mandarlo, datos y acciones.
// `qrImage` es la imagen (data:image/png;base64,…) que devuelve el backend.
const SaleTicket = ({ sale, qrImage, children }) => {
  const [message, setMessage] = useState('');
  const state = saleStatus(sale);
  const filename = `ticket-${sale.ticketNumber}.png`;

  const canShareFile = typeof navigator !== 'undefined'
    && typeof navigator.canShare === 'function'
    && typeof File !== 'undefined'
    && navigator.canShare({ files: [new File([''], filename, { type: 'image/png' })] });

  const download = async () => {
    if (!qrImage) return;
    const link = document.createElement('a');
    link.href = qrImage;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const share = async () => {
    setMessage('');
    try {
      const blob = await dataUrlToBlob(qrImage);
      const file = new File([blob], filename, { type: 'image/png' });
      await navigator.share({
        files: [file],
        title: `Ticket #${sale.ticketNumber} · ${sale.activityName}`,
        text: `Ticket #${sale.ticketNumber} de ${sale.activityName}, ${sale.date} ${sale.time}. Muestra este QR al entrar.`,
      });
    } catch (e) {
      // AbortError = la persona cerró el menú de compartir; no es un fallo.
      if (e && e.name !== 'AbortError') setMessage('No se pudo compartir. Descarga el QR y envíalo por tu app.');
    }
  };

  return (
    <>
      <PrintOnlyTicket />
      <TicketCard className="sale-ticket-print" aria-label={`Ticket ${sale.ticketNumber}`}>
        <div className="ticket-head">
          <span className="eyebrow">Ticket</span>
          <div className="number">#{sale.ticketNumber}</div>
          {sale.empresaName && <div className="company">{sale.empresaName}</div>}
        </div>
        <div className="ticket-body">
          <div className="qr-wrap">
            {qrImage ? (
              <img src={qrImage} alt={`Código QR del ticket ${sale.ticketNumber}`} />
            ) : (
              <p className="qr-note">No se pudo cargar el QR. Ábrelo de nuevo desde el detalle de la venta.</p>
            )}
            <p className="qr-note">
              Este QR solo valida la entrada. El pago ya se hizo en persona; no se cobra al escanearlo.
            </p>
            <StatusBadge status={state.status}>{state.label}</StatusBadge>
          </div>

          <SaleDetails sale={sale} />

          <TicketActions className="no-print">
            <Button variant="secondary" onClick={download} disabled={!qrImage}>Descargar QR</Button>
            {canShareFile && qrImage && <Button variant="secondary" onClick={share}>Compartir</Button>}
            <Button variant="secondary" onClick={() => window.print()}>Imprimir ticket</Button>
          </TicketActions>
          {message && <p className="no-print" role="status" style={{ marginTop: 10, fontSize: 'var(--text-sm)', color: 'var(--danger-dark)' }}>{message}</p>}
        </div>
      </TicketCard>
      {children}
    </>
  );
};

export default SaleTicket;
